import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  setDeleteProjectCandidateId,
  setEditProjectCandidateId,
  setShowAddEditProjectModal,
  setShowDeleteProjectModal,
} from "@/store/projectsSlice";
import { Project } from "@/utils/types";
import { Pencil1Icon } from "@radix-ui/react-icons";
import { formatDistanceToNow } from "date-fns";
import {
  Blocks,
  Ellipsis,
  Grid2X2,
  LayoutPanelLeft,
  LayoutPanelTop,
  Link as LucideLink,
  PanelsTopLeft,
  TrashIcon,
  type LucideIcon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router";
import { Button } from "../ui/button";

interface Props {
  project: Project;
  lastUpdated: string | null;
  onClick: (id: string) => void;
}

const ProjectDropdownMenu = React.memo(({ project }: { project: Project }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();

  const handleEditClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(setEditProjectCandidateId(project.id));
    dispatch(setShowAddEditProjectModal(true));
    setIsOpen(false);
  };

  const handleDeleteClick = (e) => {
    dispatch(setDeleteProjectCandidateId(project.id));
    dispatch(setShowDeleteProjectModal(true));
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="absolute right-2 top-2"
        >
          <Ellipsis className="size-4 text-secondary-n-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <DropdownMenuItem className="text-xs cursor-pointer" asChild>
          <Link to={`/?project_id=${project.id}`} target="_blank">
            <LucideLink className="mr-2 h-3 w-3" />
            Open in New Tab
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleEditClick}
          className="text-xs cursor-pointer"
        >
          <Pencil1Icon className="mr-2 h-3 w-3" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleDeleteClick}
          className="text-xs text-destructive hover:text-destructive cursor-pointer"
        >
          <TrashIcon className="mr-2 h-3 w-3" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export const ProjectCard = ({ project, lastUpdated, onClick }: Props) => {
  const [PlaceholderIcon, setPlaceholderIcon] = useState<LucideIcon | null>(
    null,
  );

  useEffect(() => {
    const placeholderIcons: LucideIcon[] = [
      Blocks,
      Grid2X2,
      LayoutPanelTop,
      LayoutPanelLeft,
    ];
    setPlaceholderIcon(
      () =>
        placeholderIcons[Math.floor(Math.random() * placeholderIcons.length)],
    );
  }, []);

  return (
    <div className="relative" data-testid="project-card">
      <ProjectDropdownMenu project={project} />
      <Link
        to={`/?project_id=${project.id}`}
        className="rounded-lg p-1 ring-primary-0 hover:ring-1 flex flex-col gap-1"
      >
        <div className="aspect-video rounded-md bg-secondary flex items-center justify-center overflow-hidden">
          {project.preview_url ? (
            <img
              src={project.preview_url}
              alt="Project Preview"
              className="w-full h-full object-cover"
            />
          ) : (
            PlaceholderIcon && (
              <PlaceholderIcon className="w-16 h-16 text-secondary-n-800" />
            )
          )}
        </div>
        <div className="flex justify-between p-3 items-center">
          <div className="flex flex-col gap-1">
            <h3
              className="text-sm font-medium text-foreground"
              data-testid="project-card-name"
            >
              {project.name}
            </h3>
            {lastUpdated && (
              <p className="text-xs text-secondary-n-400">
                Edited {formatDistanceToNow(new Date(lastUpdated))} ago
              </p>
            )}
          </div>
          <Button
            variant="secondary"
            size="icon-sm"
            onClick={() => onClick(project.id)}
            aria-label="Open project"
            asChild
          >
            <div>
              <PanelsTopLeft className="w-4 h-4 text-icons-secondary" />
            </div>
          </Button>
        </div>
      </Link>
    </div>
  );
};
